import React, { useState, useCallback } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { draftToMarkdown } from 'markdown-draft-js'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import { dollarsToCents } from 'lib/utils/number'
import { validationSchema } from './validation'
import makeDefaultValues from './makeDefaultValues'
import { useStyles } from './styles'
import FormSectionContainer from './form-section-container'
import EditEventSectionNav, {
  EDIT_EVENT_SECTIONS,
  getActiveSectionIndex,
  getSectionIdFromHash,
} from './edit-event-section-nav'
import EditEventPagination from './edit-event-pagination'
import EventNameAndTags from './form-sections/event-name-and-tags'
import EventLocation from './form-sections/event-location'
import ScheduleSessions from './form-sections/schedule-sessions'
import RegistrationCloseDay, {
  NO_REGISTRATION_CLOSE,
} from './form-sections/registration-close-day'
import MaxAttendance from './form-sections/max-attendance'
import AccessCode from './form-sections/access-code'
import EventDescription from './form-sections/event-description'
import FeaturedImage from './form-sections/featured-image'
import Fees from './form-sections/fees'
import ExternalRegistrationUrl from './form-sections/event-external-registration-url'
import SessionList from './form-sections/session-list'
import Unlisted from './form-sections/unlisted'
import {
  composeMaxAttendance,
  isRecurringPayment,
  ALL_AGE_GROUP_TAGS,
  LEGACY_ADULTS_ONLY_TAG,
  LEGACY_JUNIORS_ONLY_TAG,
  MANUAL_PAYMENT,
  ONE_TIME_PAYMENT,
} from 'utils/groupEventUtils'
import AssistantCoaches from './form-sections/assistant-coaches'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import AgeGroupSelect from 'components/age-group-select'
import {
  getOfferingsPath,
  OFFERINGS_TAB,
} from 'pages/pga-coach/bookings/offerings/routes'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'

const UPDATE_GROUP_EVENT = gql`
  mutation UpdateGroupEvent(
    $id: ID!
    $values: GroupEventInput!
    $reason: String
  ) {
    updateGroupEvent(id: $id, values: $values, reason: $reason) {
      success
      message
    }
  }
`

const EditEventLayout = ({ event, coachFacilities, paymentAccounts }) => {
  const isAcademyEvent = event?.academy?.id
  const isPgaHopeEvent = event?.tags.includes('PGA HOPE')

  const eligibleFacilities = isAcademyEvent
    ? event.academy?.facilities
    : coachFacilities

  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const history = useHistory()
  const { hash } = useLocation()
  const [isUpdateReasonModalOpen, setIsUpdateReasonModalOpen] = useState(false)
  const [updateReason, setUpdateReason] = useState('')
  const [activeSection, setActiveSection] = useState(() =>
    getSectionIdFromHash(hash),
  )
  const activeStepIndex = getActiveSectionIndex(activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === EDIT_EVENT_SECTIONS.length - 1
  const currentSection = EDIT_EVENT_SECTIONS[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const getStepFields = useCallback(
    section => {
      if (section.id === 'edit-section-basics') {
        return ['title', 'tags']
      }

      if (section.id === 'edit-section-location') {
        return ['locationId']
      }

      if (section.id === 'edit-section-schedule') {
        const scheduleFields = ['sessions']

        if (event.externalRegistrationUrl) {
          scheduleFields.push('externalRegistrationUrl')
        }

        if (isRecurringPayment(event)) {
          return scheduleFields.filter(field => field !== 'sessions')
        }

        return scheduleFields
      }

      if (section.id === 'edit-section-registration') {
        const registrationFields = ['maxAttendance', 'accessCode']

        if (!isPgaHopeEvent) {
          registrationFields.push('price', 'paymentAccountId')
        }

        if (!event.externalRegistrationUrl) {
          registrationFields.push('hoursBeforeRegistrationCloses')
        }

        return registrationFields
      }

      if (section.id === 'edit-section-media') {
        return ['featuredImageUrl', 'unlisted']
      }

      return section.fields || []
    },
    [event, isPgaHopeEvent],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop])

  const [updateGroupEvent, { loading }] = useMutation(UPDATE_GROUP_EVENT, {
    onCompleted: data => {
      if (data.updateGroupEvent.success) {
        enqueueSnackbar('Event updated successfully', {
          variant: 'success',
        })
        setTimeout(() => {
          history.push(getOfferingsPath(OFFERINGS_TAB.GROUP_EVENTS))
        }, 1000)
      } else {
        enqueueSnackbar(data.updateGroupEvent.message, {
          variant: 'error',
        })
      }
    },
  })

  const form = useForm({
    defaultValues: makeDefaultValues(event),
    shouldUnregister: false,
    resolver: yupResolver(
      validationSchema(eligibleFacilities, paymentAccounts, event),
    ),
  })

  const {
    formState: { dirtyFields },
    watch,
    trigger,
  } = form

  const navigateToSectionWithError = useCallback(
    formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = EDIT_EVENT_SECTIONS.find(section => {
        const fields = getStepFields(section)
        return fields.some(field => errorFields.includes(field))
      })

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    },
    [getStepFields, scrollToFormTop],
  )

  const showFirstValidationError = useCallback(
    formErrors => {
      const errorMessage = getFirstErrorMessage(formErrors)

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const formErrors = form.formState.errors
    showFirstValidationError(formErrors)
  }, [
    activeStepIndex,
    currentSection,
    form.formState.errors,
    getStepFields,
    isLastStep,
    scrollToFormTop,
    showFirstValidationError,
    trigger,
  ])

  const selectedFacilityId = watch('locationId')
  const selectedFacility = coachFacilities?.find(
    facility => facility.id === selectedFacilityId,
  )

  const handleSaveClick = async e => {
    e.preventDefault()

    const isValid = await form.trigger()

    if (!isValid) {
      const formErrors = form.formState.errors
      navigateToSectionWithError(formErrors)
      showFirstValidationError(formErrors)
      return
    }

    if (dirtyFields.locationId || dirtyFields.sessions) {
      setIsUpdateReasonModalOpen(true)
    } else {
      form.handleSubmit(onSubmit)(e)
    }
  }

  const onUpdateReasonChange = event => {
    setUpdateReason(event.target.value)
  }

  const handleUpdateDialogClose = () => {
    setIsUpdateReasonModalOpen(false)
    setUpdateReason('')
  }

  const onSubmit = async formValues => {
    const ageGroupMap = new Map([
      ['adults-only', LEGACY_ADULTS_ONLY_TAG],
      ['juniors-only', LEGACY_JUNIORS_ONLY_TAG],
    ])
    const filteredTags = formValues.tags.filter(
      tag => !ALL_AGE_GROUP_TAGS.includes(tag),
    )
    const ageGroupValue = ageGroupMap.get(formValues.ageGroup)
    if (ageGroupValue) {
      filteredTags.push(ageGroupValue)
    }
    const selectedFacility = eligibleFacilities.find(
      cf => cf.id === formValues.locationId,
    )

    const hasPaymentAccountId = (formValues.paymentAccountId || '').length > 0
    let registrationCloseDateTime = null

    if (formValues.registrationCloseDate && formValues.registrationCloseTime) {
      registrationCloseDateTime = buildDateTime(
        formValues.registrationCloseDate,
        formValues.registrationCloseTime,
        selectedFacility.timezone,
      )
    }

    if (formValues.hoursBeforeRegistrationCloses === NO_REGISTRATION_CLOSE) {
      registrationCloseDateTime = null
    } else if (formValues.hoursBeforeRegistrationCloses !== undefined) {
      const eventStartDateTime = buildDateTime(
        formValues.sessions[0].startDate,
        formValues.sessions[0].startTime,
        selectedFacility.timezone,
      )

      registrationCloseDateTime = eventStartDateTime.minus({
        hours: Number(formValues.hoursBeforeRegistrationCloses),
      })
    }

    return updateGroupEvent({
      variables: {
        id: event.id,
        reason: updateReason,
        values: {
          title: formValues.title,
          academyId: event.academy?.id || null,
          assistantCoachIds: formValues.assistantCoachIds,
          description: formValues.description
            ? draftToMarkdown(JSON.parse(formValues.description))
            : null,
          registrationCloseDateTime,
          tags: filteredTags,
          externalRegistrationUrl: formValues.externalRegistrationUrl,
          maxAttendance: composeMaxAttendance(
            formValues.maxAttendance,
            event.hasWaitlist,
          ),
          priceInCents: dollarsToCents(formValues.price),
          featuredImageUrl: formValues.featuredImageUrl,
          paymentAccountId: hasPaymentAccountId
            ? formValues.paymentAccountId
            : null,
          paymentRecurrenceSchedule: isRecurringPayment(event)
            ? event.paymentRecurrenceSchedule
            : hasPaymentAccountId
              ? ONE_TIME_PAYMENT
              : MANUAL_PAYMENT,
          sessions: isRecurringPayment(event)
            ? event.sessions.map(session => ({
              facilityId: selectedFacility.id,
              startDateTime: session.startDateTime,
              endDateTime: session.endDateTime,
            }))
            : formValues.sessions.map(session => ({
              facilityId: selectedFacility.id,
              startDateTime: buildDateTime(
                session.startDate,
                session.startTime,
                selectedFacility.timezone,
              ),
              endDateTime: buildDateTime(
                session.startDate,
                session.endTime,
                selectedFacility.timezone,
              ),
            })),
          unlisted: formValues.unlisted,
          accessCode:
            (formValues.accessCode || '').length > 0
              ? formValues.accessCode
              : null,
        },
      },
    })
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'edit-section-basics':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EventNameAndTags />
            <Box className={classes.fieldGroup}>
              <AgeGroupSelect className={classes.fieldInput} />
            </Box>
            <EventDescription />
          </FormSectionContainer>
        )
      case 'edit-section-location':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EventLocation event={event} coachFacilities={eligibleFacilities} />
          </FormSectionContainer>
        )
      case 'edit-section-schedule':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <AssistantCoaches event={event} />
            {event.externalRegistrationUrl ? (
              <Box className={classes.fieldGroup}>
                <ExternalRegistrationUrl />
              </Box>
            ) : null}
            {isRecurringPayment(event) ? (
              <Box className={classes.fieldGroup}>
                <Typography className={classes.fieldHelper}>
                  Subscription session dates/times cannot be edited. To make
                  changes, this offering will need to be duplicated and
                  cancelled.
                </Typography>
                <SessionList
                  event={event}
                  timezone={selectedFacility?.timezone}
                />
              </Box>
            ) : (
              <ScheduleSessions timezone={selectedFacility?.timezone} />
            )}
          </FormSectionContainer>
        )
      case 'edit-section-registration':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <RegistrationCloseDay
              eventStartDateTime={event.startDateTime}
              isExternalRegistration={!!event.externalRegistrationUrl}
              timezone={selectedFacility?.timezone || event.timezone}
            />
            <MaxAttendance />
            <AccessCode />
            {!isPgaHopeEvent ? (
              <Box className={classes.fieldGroup}>
                <Fees event={event} paymentAccounts={paymentAccounts} />
              </Box>
            ) : null}
          </FormSectionContainer>
        )
      case 'edit-section-media':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <FeaturedImage />
            <Unlisted />
          </FormSectionContainer>
        )
      default:
        return null
    }
  }

  return (
    <Box>
      <FormProvider {...form}>
        <form onSubmit={handleSaveClick}>
          <Box className={classes.actionBar}>
            <Button
              type="button"
              className={classes.cancelAction}
              startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
              onClick={() => history.goBack()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveAction}
            >
              Save
            </Button>
          </Box>

          {loading ? (
            <LinearProgress color="primary" className={classes.loadingStyles} />
          ) : null}
          <Container maxWidth="lg">
            <Box pt={4} pb={3}>
              <Box className={classes.layoutGrid}>
                <Box className={classes.navColumn}>
                  <EditEventSectionNav
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                  />
                </Box>

                <Box className={classes.formColumn}>
                  {renderActiveSection()}
                  <EditEventPagination
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </form>
      </FormProvider>
      <Dialog maxWidth="sm" fullWidth open={isUpdateReasonModalOpen}>
        <DialogTitle>This update will notify event attendees</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Updating the <strong>Location</strong> or the{' '}
            <strong>Schedule</strong> of an event will automatically inform the
            attendees of the changes. Use the form below if you would like to
            add a custom message.
          </Typography>
          <br />
          <Typography gutterBottom>
            <strong>Provide a Reason</strong> (optional)
          </Typography>
          <TextField
            multiline
            rows={4}
            name="updateReason"
            label="Reason for Update"
            onChange={onUpdateReasonChange}
            gutterBottom
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
          >
            Update Event
          </Button>
          <Button color="primary" onClick={handleUpdateDialogClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EditEventLayout
